import { orderBy } from 'lodash';
import { useCallback, useEffect } from 'react';

import { queryClient } from '@/src/lib/react-query/react-query';

import { filterCountersKeys } from '../../../api/getFilterCounters';
import { useInbox } from '../../../store/inbox.store';
import { InboxEntry, InboxEntryType, StatusFilterEnum, View } from '../../../types';
import {
  isPermanentlyDeletedSocketData,
  PermanentlyDeletedSocketData,
} from '../../../types/PermanentlyDeletedSocketData';
import { isStaleEntry, StaleEntry } from '../../../types/StaleEntry';
import { fileDetailsKeys } from '../../EntryPanel/FileEntryViewer/api/getFileDetails';
import { inboxEntriesKeys } from '../api/getInboxEntries';
import { mapInboxEntries } from '../Sidebar';

export const getDestinationView = (trash, filter) => (trash ? View.Trash : filter);

const deleteFromListOrInvalidate = ({
  inboxEntryId,
  filter,
  projectId,
}: {
  inboxEntryId: number;
  filter: StatusFilterEnum;
  projectId: string;
}) => {
  queryClient.setQueryData([...inboxEntriesKeys.lists({ projectId, filter })], (prev: any) => {
    return {
      ...prev,
      pages: prev.pages.map((page) => {
        return {
          ...page,
          data: page.data.filter((ie) => ie['source-id'] !== inboxEntryId),
        };
      }),
    };
  });
};

type UpdateOrInsertStaleEntry = {
  staleEntry: StaleEntry;
  existingInboxEntry: InboxEntry;
  filter: StatusFilterEnum;
};

type useInboxEntriesSocketSubscriptionsProps = {
  projectId: string;
  filter: StatusFilterEnum;
  view: View;
  onNotifyUpdates: () => void;
  onShowUpdateToast: () => void;
};

export const useInboxEntriesSocketSubscriptions = ({
  projectId,
  filter,
  view,
  onNotifyUpdates,
  onShowUpdateToast,
}: useInboxEntriesSocketSubscriptionsProps) => {
  const { socketData } = useInbox();

  const handlePermanentlyDeleted = useCallback(
    (staleEntry: PermanentlyDeletedSocketData) => {
      deleteFromListOrInvalidate({
        inboxEntryId: staleEntry.payload['inbox-entry-source-id'],
        projectId,
        filter,
      });

      if (!staleEntry.isSameUser) {
        onShowUpdateToast();
      }
    },
    [projectId, filter, onShowUpdateToast]
  );

  const handleInserted = useCallback(
    ({ staleEntry, existingInboxEntry, filter }: UpdateOrInsertStaleEntry) => {
      const destinationView = getDestinationView(staleEntry.data.rawIboxEntry.trash, filter);

      if (!existingInboxEntry) {
        queryClient.setQueryData(
          [...inboxEntriesKeys.lists({ projectId, filter: destinationView })],
          (prev: any) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: orderBy(
                    [staleEntry.data.rawIboxEntry, ...page.data].map((inboxEntry) => {
                      if (inboxEntry['source-id'] === staleEntry.data.rawIboxEntry['source-id']) {
                        return staleEntry.data.rawIboxEntry;
                      }
                      return inboxEntry;
                    }),
                    ['date'],
                    ['desc']
                  ),
                };
              }),
            };
          }
        );

        if (staleEntry.type === InboxEntryType.DOCUMENT) {
          queryClient.invalidateQueries([
            ...fileDetailsKeys.detail({ fileId: staleEntry.data.rawEntry.id }),
          ]);
        }

        // Sync with status counters
        // if (staleEntry.type === InboxEntryType.DOCUMENT) {
        //   queryClient.setQueryData(
        //     [...filterCountersKeys.detail(InboxEntryStatus.New, projectId)],
        //     (prev: any) => {
        //       return {
        //         ...prev,
        //         data: prev.data + 1,
        //       };
        //     }
        //   );
        // } else {
        //   // We need a way to determine that the latest socket data is from external which introduces + 1
        //   // Else if just invalidate
        //   queryClient.invalidateQueries([...filterCountersKeys.list]);
        // }

        // Sync with status counters
        queryClient.invalidateQueries([...filterCountersKeys.list]);

        if (!staleEntry.isSameUser && view === destinationView) {
          onNotifyUpdates();
        }
      } else {
        // Email replies will fall into this scenario
        queryClient.setQueryData(
          [...inboxEntriesKeys.lists({ projectId, filter: destinationView })],
          (prev: any) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: orderBy(
                    page.data.map((inboxEntry) => {
                      if (inboxEntry['source-id'] === staleEntry.data.rawIboxEntry['source-id']) {
                        return staleEntry.data.rawIboxEntry;
                      }
                      return inboxEntry;
                    }),
                    ['date'],
                    ['desc']
                  ),
                };
              }),
            };
          }
        );

        queryClient.invalidateQueries([...filterCountersKeys.list]);

        if (!staleEntry.isSameUser) {
          onShowUpdateToast();
        }
      }
    },
    [view, projectId, onNotifyUpdates, onShowUpdateToast]
  );

  const handleUpdated = useCallback(
    ({ staleEntry, existingInboxEntry, filter }: UpdateOrInsertStaleEntry) => {
      if (!existingInboxEntry) {
        return;
      }

      const isDeleted = !existingInboxEntry.data.isDeleted && staleEntry.data.rawIboxEntry.trash; // moved to trash
      const isRestored = existingInboxEntry.data.isDeleted && !staleEntry.data.rawIboxEntry.trash;

      if (isDeleted || isRestored) {
        deleteFromListOrInvalidate({
          inboxEntryId: staleEntry.data.rawIboxEntry['source-id'],
          projectId,
          filter,
        });

        // @TODO: check cache first if existing in destination page, then +-
        // Destination should be invalidated
        const destinationView = getDestinationView(staleEntry.data.rawIboxEntry.trash, filter);
        queryClient.invalidateQueries([
          ...inboxEntriesKeys.lists({ projectId, filter: destinationView }),
        ]);
        queryClient.invalidateQueries([...filterCountersKeys.list]);
      } else {
        queryClient.setQueryData(
          [...inboxEntriesKeys.lists({ projectId, filter })],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: orderBy(
                    page.data.map((inboxEntry) => {
                      if (inboxEntry['source-id'] === staleEntry.data.rawIboxEntry['source-id']) {
                        return staleEntry.data.rawIboxEntry;
                      }
                      return inboxEntry;
                    }),
                    ['date'],
                    ['desc']
                  ),
                };
              }),
            };
          }
        );
        queryClient.invalidateQueries([...filterCountersKeys.list]);
      }

      // For email entries, we invalidate the other page (all <-> trash)
      if (existingInboxEntry.type === InboxEntryType.THREAD) {
        // Destination filter should be invalidated
        const destinationView = getDestinationView(
          staleEntry.data.rawEntry.rawEmailEntry.trash,
          filter
        );
        if (view !== destinationView) {
          queryClient.invalidateQueries([
            ...inboxEntriesKeys.lists({ projectId, filter: destinationView }),
          ]);
        }
      }

      // Sync status counters only when status changed in the update
      if (existingInboxEntry.data.status !== staleEntry.data.rawIboxEntry.status) {
        queryClient.invalidateQueries([...filterCountersKeys.list]);
      }

      if (!staleEntry.isSameUser) {
        onShowUpdateToast();
      }
    },
    [projectId, filter, view, onShowUpdateToast]
  );

  useEffect(() => {
    if (!socketData) {
      return;
    }

    // @TODO: can convert to simple pages.flatmap
    const unmappedInboxEntries = queryClient.getQueryData<InboxEntry[]>(
      inboxEntriesKeys.lists({ projectId, filter })
    );

    if (!unmappedInboxEntries) return;

    const inboxEntries = mapInboxEntries(unmappedInboxEntries);

    if (inboxEntries.length < 1) {
      return;
    }

    // Scenario: Deleted (permanent delete)
    if (socketData.payload.deleted && isPermanentlyDeletedSocketData(socketData)) {
      handlePermanentlyDeleted(socketData);
      return;
    }

    if (!isStaleEntry(socketData)) {
      return;
    }

    const existingInboxEntry = inboxEntries.find(
      (ie) => ie.data.id === socketData.payload['inbox-entry-source-id']
    );

    // Scenario: New file upload, or new reply or restored
    if (socketData.payload.inserted || !existingInboxEntry) {
      handleInserted({ existingInboxEntry, staleEntry: socketData, filter });
      return;
    }

    // Scenario: Update
    handleUpdated({ existingInboxEntry, staleEntry: socketData, filter });
  }, [socketData, handleInserted, handleUpdated, handlePermanentlyDeleted, filter, projectId]);
};
